import * as THREE from "three";

// Plan-space helpers shared by the 3D stone, cabinets and walls (x right, y down, inches).

export const insidePoly = ([x, y], pts) => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
};

// Outline of a sink / cooktop cutout in piece space
export const cutoutRing = (c) => {
  const w = Number(c.w) || 6;
  const h = Number(c.h) || 6;
  const cx = c.x + w / 2;
  const cy = c.y + h / 2;
  const shape = c.shape || "Rectangle";
  let ring;
  if (shape === "Oval" || shape === "Round") {
    const rx = shape === "Round" ? Math.min(w, h) / 2 : w / 2;
    const ry = shape === "Round" ? rx : h / 2;
    ring = Array.from({ length: 36 }, (_, i) => {
      const t = (i / 36) * Math.PI * 2;
      return [cx + Math.cos(t) * rx, cy + Math.sin(t) * ry];
    });
  } else {
    ring = [
      [c.x, c.y],
      [c.x + w, c.y],
      [c.x + w, c.y + h],
      [c.x, c.y + h],
    ];
  }
  const r = ((Number(c.rot) || 0) * Math.PI) / 180;
  if (!r) return ring;
  return ring.map(([x, y]) => [
    cx + (x - cx) * Math.cos(r) - (y - cy) * Math.sin(r),
    cy + (x - cx) * Math.sin(r) + (y - cy) * Math.cos(r),
  ]);
};

// Midpoint, length, outward normal and yaw of edge i. `flipped` is true when the
// outward side is on the right of the edge direction (the frame is then left-handed).
export const edgeFrame = (pts, i) => {
  const a = pts[i];
  const b = pts[(i + 1) % pts.length];
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy) || 1;
  let nx = -dy / len;
  let ny = dx / len;
  let flipped = false;
  const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  if (insidePoly([mid[0] + nx * 0.5, mid[1] + ny * 0.5], pts)) {
    nx = -nx;
    ny = -ny;
    flipped = true;
  }
  return { mid, len, nx, ny, flipped, rotY: -Math.atan2(dy, dx) };
};

// plan (x right, y down) -> shape (x, -y); extruded along +z then tipped so z becomes up
export const shapeFrom = (pts, holes = []) => {
  const s = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, -y)));
  holes.forEach((h) =>
    s.holes.push(new THREE.Path(h.map(([x, y]) => new THREE.Vector2(x, -y)))),
  );
  return s;
};

export const scaleAbout = (pts, k) => {
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return pts.map(([x, y]) => [cx + (x - cx) * k, cy + (y - cy) * k]);
};

export const lineIntersect = ([x1, y1], [x2, y2], [x3, y3], [x4, y4]) => {
  const d = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(d) < 1e-9) return null;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / d;
  return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
};

// polygon pulled inward by d[i] on each edge; null when the outline is too tight for it
export const insetPolygon = (pts, d) => {
  const n = pts.length;
  const lines = pts.map((a, i) => {
    const b = pts[(i + 1) % n];
    const f = edgeFrame(pts, i);
    const off = d[i] || 0;
    return {
      a: [a[0] - f.nx * off, a[1] - f.ny * off],
      b: [b[0] - f.nx * off, b[1] - f.ny * off],
    };
  });
  const out = [];
  for (let i = 0; i < n; i++) {
    const l1 = lines[(i - 1 + n) % n];
    const l2 = lines[i];
    const p = lineIntersect(l1.a, l1.b, l2.a, l2.b);
    if (!p) return null;
    out.push(p);
  }
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const a2 = out[i];
    const b2 = out[(i + 1) % n];
    if ((b[0] - a[0]) * (b2[0] - a2[0]) + (b[1] - a[1]) * (b2[1] - a2[1]) <= 0)
      return null;
  }
  return out;
};

// true when vertex i of the outline points outward (convex), whichever way it winds
export const convexAt = (pts, i) => {
  const n = pts.length;
  const area2 = pts.reduce((s, p, k) => {
    const q = pts[(k + 1) % n];
    return s + p[0] * q[1] - q[0] * p[1];
  }, 0);
  const a = pts[(i - 1 + n) % n];
  const b = pts[i];
  const c = pts[(i + 1) % n];
  const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
  return cross * area2 > 0;
};
